import { ReactElement } from 'react';
import { useUpdateEffect } from 'react-use';

import {
   ReceptionServiceWithPractitionersQuery,
   ReceptionServiceWithPractitionersSimple,
} from '@dreryk/egabinet-private-medical-service-frontend-api';
import {
   Practitioner as WaitlistPractitioner,
   ReceptionService as WaitlistReceptionService,
} from '@dreryk/egabinet-waitlist-frontend-api';
import { PractitionerView } from '@dreryk/egabinet-personnel-frontend-api';

import { BindingCreator, createTranslatedText, FormContextValues } from '@dreryk/react-common';
import { Select } from '@dreryk/react-components';
import { refetchCqrsQuery } from '@dreryk/command-query-react-common';

import { translationKeys } from '../../../translations';
import { WaitlistFormController } from '../../WaitlistForm.types';
import { isPractitionerView } from '../PractitionerField/PractitionerView.utils';

import { createReceptionServiceWithPractitionerSearchCreator, triggerLabelExtractor } from './ServiceField.utils';
import { ServiceFieldLoader } from './ServiceField.loader';
import { IncompatibleServiceStatus } from './IncompatibleServiceStatus';
import { ServiceNotAvailableInLocationStatus } from './ServiceNotAvailableInLocationStatus';

type ServiceFormBeanPart = {
   service: WaitlistReceptionService | null;
   practitioner?: WaitlistPractitioner | PractitionerView | null;
   videoConsultation: boolean;
};

type ServiceFieldProps = {
   readonly register: BindingCreator<ServiceFormBeanPart, 'service'>;
   readonly watch: WaitlistFormController['watch'];
   readonly setValue: FormContextValues<ServiceFormBeanPart>['setValue'];
};

export const ServiceField = (props: ServiceFieldProps): ReactElement => {
   const practitioner = props.watch('practitioner');
   const currentVideoConsultation = props.watch('videoConsultation');
   const { service: currentService, location: currentLocation } = props.watch(['service', 'location']);
   const practitionerId =
      practitioner && isPractitionerView(practitioner) ? practitioner.id : practitioner?.id?.asNumber();

   useUpdateEffect(() => {
      if (currentService) {
         refetchCqrsQuery(ReceptionServiceWithPractitionersQuery);
      }
   }, [currentLocation]);

   useUpdateEffect(() => {
      props.setValue('service', null);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [practitionerId]);

   return (
      <>
         <ServiceFieldLoader practitionerId={practitionerId} currentServiceId={currentService?.id}>
            {({ servicesExist, currentServiceLocationIds }) => (
               <>
                  <Select<ReceptionServiceWithPractitionersSimple>
                     testId={'service'}
                     triggerLabelExtractor={triggerLabelExtractor}
                     controller={props.register('service')}
                     placeholder={translationKeys.chooseService}
                     label={translationKeys.service}
                     disabled={servicesExist === false}
                     searchInputEnabled
                  >
                     <Select.SearchCreator
                        searchCreator={createReceptionServiceWithPractitionerSearchCreator(practitionerId)}
                     >
                        {({ items: services }) => (
                           <Select.List<ReceptionServiceWithPractitionersSimple> items={services} keyExtractor={'id'}>
                              {(service) => (
                                 <Select.Item
                                    item={service}
                                    testId={service.id.asNumber()}
                                    label={<Select.Highlight node={createTranslatedText(service.name)} />}
                                 />
                              )}
                           </Select.List>
                        )}
                     </Select.SearchCreator>
                  </Select>
                  {currentLocation ? (
                     <ServiceNotAvailableInLocationStatus
                        currentLocationId={currentLocation?.id}
                        currentServiceLocationIds={currentServiceLocationIds}
                     />
                  ) : null}
               </>
            )}
         </ServiceFieldLoader>
         <IncompatibleServiceStatus service={currentService} videoConsultationSelected={currentVideoConsultation} />
      </>
   );
};
